import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ReactFlow,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel,
  useReactFlow,
  ConnectionLineType,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import axios from "axios";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Play,
  Save,
  Plus,
  Upload,
  FileImage,
  History,
  RefreshCw,
  Settings,
  HelpCircle,
  Share2,
  Download,
  Maximize2,
  Trash2,
  Type,
  Image as ImageIcon,
  Sparkles,
  Wand2,
  Palette,
  Copy,
  Loader2,
  X,
  MoreHorizontal,
  ChevronLeft,
  Layers,
  Zap,
} from "lucide-react";

// Custom node components
import VisualImageNode from "../../components/ai-workflows/canvas/VisualImageNode";
import VisualTextNode from "../../components/ai-workflows/canvas/VisualTextNode";
import VisualGenerateNode from "../../components/ai-workflows/canvas/VisualGenerateNode";
import VisualTransformNode from "../../components/ai-workflows/canvas/VisualTransformNode";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Node types for React Flow
const nodeTypes = {
  imageInput: VisualImageNode,
  textInput: VisualTextNode,
  generate: VisualGenerateNode,
  transform: VisualTransformNode,
  enhance: VisualTransformNode,
  style: VisualTransformNode,
  output: VisualImageNode,
};

// Default edge options
const defaultEdgeOptions = {
  type: "smoothstep",
  animated: true,
  style: { stroke: "#3D6355", strokeWidth: 2 },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: "#3D6355",
  },
};

// Connection line style
const connectionLineStyle = {
  stroke: "#3D6355",
  strokeWidth: 2,
};

export default function VisualCanvasBoard() {
  const { workflowId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const reactFlowWrapper = useRef(null);
  const { screenToFlowPosition, fitView } = useReactFlow();

  // State
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  // Load workflow
  useEffect(() => {
    if (workflowId && workflowId !== "new") {
      loadWorkflow();
    } else {
      // Create new workflow
      createNewWorkflow();
    }
  }, [workflowId]);

  const loadWorkflow = async () => {
    try {
      const res = await axios.get(`${API}/ai-workflows/boards/${workflowId}`, { headers });
      setWorkflow(res.data);
      
      // Also load the latest run results to display generated images
      let latestRunOutput = {};
      try {
        const runsRes = await axios.get(`${API}/ai-workflows/boards/${workflowId}/runs?limit=1`, { headers });
        const runs = runsRes.data.runs || [];
        if (runs.length > 0 && runs[0].status === "success") {
          const runDetails = await axios.get(`${API}/ai-workflows/runs/${runs[0].id}`, { headers });
          latestRunOutput = runDetails.data.output_snapshot || {};
        }
      } catch (e) {
        console.log("No previous runs found");
      }
      
      // Convert to React Flow nodes
      const flowNodes = (res.data.nodes || []).map((node) => {
        // Check if this node has output from the latest run
        const nodeOutput = latestRunOutput[node.id] || {};
        const config = { ...(node.config || {}), ...nodeOutput };
        
        return {
          id: node.id,
          type: getNodeType(node.type),
          position: node.position || { x: 0, y: 0 },
          data: {
            label: node.label,
            nodeType: node.type,
            config: config,
            onDelete: () => deleteNode(node.id),
            onUpdate: (config) => updateNodeConfig(node.id, config),
          },
        };
      });

      const flowEdges = (res.data.edges || []).map((edge) => ({
        id: edge.id,
        source: edge.source,
        sourceHandle: edge.source_handle,
        target: edge.target,
        targetHandle: edge.target_handle,
        ...defaultEdgeOptions,
      }));

      setNodes(flowNodes);
      setEdges(flowEdges);
      
      setTimeout(() => fitView({ padding: 0.2 }), 100);
    } catch (error) {
      console.error("Failed to load workflow:", error);
      toast.error("Failed to load workflow");
    } finally {
      setLoading(false);
    }
  };

  const createNewWorkflow = async () => {
    try {
      const res = await axios.post(
        `${API}/ai-workflows/boards`,
        { name: "Untitled Workflow", description: "" },
        { headers }
      );
      setWorkflow(res.data);
      window.history.replaceState(null, '', `/ai-workflows/${res.data.id}`);
      toast.success("Workflow created");
    } catch (error) {
      console.error("Failed to create workflow:", error);
      toast.error("Failed to create workflow");
    } finally {
      setLoading(false);
    }
  };

  const getNodeType = (backendType) => {
    const typeMap = {
      text_input: "textInput",
      image_input: "imageInput",
      multi_image_input: "imageInput",
      openai_image: "generate",
      replicate_run: "generate",
      prompt_enhancer: "enhance",
      style_enforcer: "style",
      output_collector: "output",
    };
    return typeMap[backendType] || "generate";
  };

  const getBackendType = (nodeType) => {
    const typeMap = {
      textInput: "text_input",
      imageInput: "image_input",
      generate: "openai_image",
      transform: "replicate_run",
      enhance: "prompt_enhancer",
      style: "style_enforcer",
      output: "output_collector",
    };
    return typeMap[nodeType] || "text_input";
  };

  // Add node to canvas
  const addNode = useCallback((type, position = null) => {
    const id = `node-${Date.now()}`;
    const pos = position || {
      x: 200 + Math.random() * 400,
      y: 200 + Math.random() * 300,
    };

    const labels = {
      textInput: "Text Prompt",
      imageInput: "Reference Image",
      generate: "Generate Image",
      transform: "Transform",
      enhance: "Enhance Prompt",
      style: "Style Transfer",
      output: "Output",
    };

    const newNode = {
      id,
      type,
      position: pos,
      data: {
        label: labels[type] || type,
        nodeType: type,
        config: {},
        onDelete: () => deleteNode(id),
        onUpdate: (config) => updateNodeConfig(id, config),
      },
    };

    setNodes((nds) => [...nds, newNode]);
  }, [setNodes]);

  // Delete node
  const deleteNode = useCallback((nodeId) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
  }, [setNodes, setEdges]);

  // Update node config
  const updateNodeConfig = useCallback((nodeId, config) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === nodeId ? { ...n, data: { ...n.data, config: { ...n.data.config, ...config } } } : n
      )
    );
  }, [setNodes]);

  // Handle connections
  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => addEdge({ ...params, ...defaultEdgeOptions }, eds));
    },
    [setEdges]
  );

  // Handle drop
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const type = event.dataTransfer.getData("application/reactflow/type");
      if (!type) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      addNode(type, position);
    },
    [screenToFlowPosition, addNode]
  );

  // Save workflow
  const saveWorkflow = async () => {
    if (!workflow) return;
    setSaving(true);

    try {
      const apiNodes = nodes.map((node) => ({
        id: node.id,
        type: getBackendType(node.type),
        label: node.data?.label || node.type,
        position: node.position,
        config: node.data?.config || {},
        input_ports: [],
        output_ports: [],
      }));

      const apiEdges = edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        source_handle: edge.sourceHandle || "output",
        target: edge.target,
        target_handle: edge.targetHandle || "input",
      }));

      await axios.patch(
        `${API}/ai-workflows/boards/${workflow.id}`,
        { nodes: apiNodes, edges: apiEdges, name: workflow.name },
        { headers }
      );
      toast.success("Saved");
    } catch (error) {
      console.error("Failed to save:", error);
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // Validate workflow connections
  const validateWorkflowConnections = useCallback(() => {
    // Node types that require inputs to function
    const nodesRequiringInput = ["generate", "transform", "enhance", "style", "output"];
    
    // Check each node that requires an input
    for (const node of nodes) {
      if (nodesRequiringInput.includes(node.type)) {
        // Check if this node has at least one incoming edge
        const hasIncomingEdge = edges.some(edge => edge.target === node.id);
        if (!hasIncomingEdge) {
          return {
            valid: false,
            error: `"${node.data?.label || node.type}" requires an input connection. Please connect it to a source node.`
          };
        }
      }
    }
    
    // Check if there's at least one "generate" node and it has a text input connected
    const generateNodes = nodes.filter(n => n.type === "generate");
    for (const genNode of generateNodes) {
      const hasTextInput = edges.some(edge => {
        const sourceNode = nodes.find(n => n.id === edge.source);
        return edge.target === genNode.id && sourceNode?.type === "textInput";
      });
      if (!hasTextInput) {
        return {
          valid: false,
          error: `"${genNode.data?.label || 'Generate Image'}" must be connected to a Text Prompt node.`
        };
      }
    }
    
    return { valid: true, error: null };
  }, [nodes, edges]);

  // Check if workflow can run
  const canRunWorkflow = useCallback(() => {
    if (!workflow || nodes.length === 0) return false;
    const validation = validateWorkflowConnections();
    return validation.valid;
  }, [workflow, nodes, validateWorkflowConnections]);

  // Run workflow
  const runWorkflow = async () => {
    if (!workflow || nodes.length === 0) {
      toast.error("Add some nodes to the canvas first");
      return;
    }
    
    // Validate connections before running
    const validation = validateWorkflowConnections();
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }
    
    await saveWorkflow();
    setRunning(true);

    try {
      const res = await axios.post(
        `${API}/ai-workflows/boards/${workflow.id}/run`,
        { inputs: {} },
        { headers }
      );
      toast.success("Workflow started");
      pollRunStatus(res.data.id);
    } catch (error) {
      console.error("Failed to run:", error);
      const errorMsg = error.response?.data?.detail || "Failed to run workflow";
      toast.error(errorMsg);
      setRunning(false);
    }
  };

  const pollRunStatus = async (runId) => {
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/ai-workflows/runs/${runId}`, { headers });
        if (res.data.status === "running" || res.data.status === "pending") {
          setTimeout(poll, 2000);
        } else {
          setRunning(false);
          if (res.data.status === "success") {
            toast.success("Workflow completed!");
            
            // Update nodes with their outputs
            const outputSnapshot = res.data.output_snapshot || {};
            setNodes((nds) =>
              nds.map((node) => {
                const nodeOutput = outputSnapshot[node.id];
                if (nodeOutput && nodeOutput.image) {
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      config: {
                        ...node.data.config,
                        image: nodeOutput.image,
                        output_image: nodeOutput.image,
                      },
                    },
                  };
                }
                return node;
              })
            );
          } else {
            toast.error("Workflow failed: " + (res.data.error_message || "Unknown error"));
          }
        }
      } catch (error) {
        setRunning(false);
      }
    };
    poll();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#000000]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="h-screen w-full bg-[#000000] flex" data-testid="visual-canvas-board">
      {/* Left Toolbar */}
      <div className="w-16 bg-[#000000] border-r border-slate-800/50 flex flex-col items-center py-4 gap-2">
        {/* Back button */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/ai-workflows")}
                className="text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Back to Workflows</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div className="w-8 h-px bg-slate-800 my-2" />

        {/* Add Node Menu */}
        <DropdownMenu>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="icon"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full w-10 h-10"
                  >
                    <Plus className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="right">Add Node</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <DropdownMenuContent side="right" className="w-56 bg-slate-900 border-slate-700">
            <DropdownMenuItem onClick={() => addNode("textInput")} className="text-slate-200 focus:bg-slate-800">
              <Type className="w-4 h-4 mr-2 text-emerald-400" />
              Text Prompt
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => addNode("imageInput")} className="text-slate-200 focus:bg-slate-800">
              <FileImage className="w-4 h-4 mr-2 text-blue-400" />
              Reference Image
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-slate-700" />
            <DropdownMenuItem onClick={() => addNode("generate")} className="text-slate-200 focus:bg-slate-800">
              <Sparkles className="w-4 h-4 mr-2 text-purple-400" />
              Generate Image (Nano Banana 2)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => addNode("transform")} className="text-slate-200 focus:bg-slate-800">
              <Wand2 className="w-4 h-4 mr-2 text-pink-400" />
              Transform
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => addNode("enhance")} className="text-slate-200 focus:bg-slate-800">
              <Zap className="w-4 h-4 mr-2 text-amber-400" />
              Enhance Prompt
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => addNode("style")} className="text-slate-200 focus:bg-slate-800">
              <Palette className="w-4 h-4 mr-2 text-cyan-400" />
              Style Transfer
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-slate-700" />
            <DropdownMenuItem onClick={() => addNode("output")} className="text-slate-200 focus:bg-slate-800">
              <ImageIcon className="w-4 h-4 mr-2 text-indigo-400" />
              Output
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Tools */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-slate-500 hover:text-white hover:bg-slate-800"
              >
                <Upload className="w-5 h-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Upload Image</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-slate-500 hover:text-white hover:bg-slate-800"
              >
                <History className="w-5 h-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">History</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-slate-500 hover:text-white hover:bg-slate-800"
              >
                <RefreshCw className="w-5 h-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Regenerate</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-slate-500 hover:text-white hover:bg-slate-800"
              >
                <Layers className="w-5 h-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Layers</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div className="flex-1" />

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowHelp(!showHelp)}
                className="text-slate-500 hover:text-white hover:bg-slate-800"
              >
                <HelpCircle className="w-5 h-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Help</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* User Avatar */}
        <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-semibold mt-2">
          {user?.name?.charAt(0) || "U"}
        </div>
      </div>

      {/* Main Canvas */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <div className="h-14 bg-[#000000]/80 backdrop-blur border-b border-slate-800/50 flex items-center justify-between px-4">
          <div className="flex items-center gap-4">
            <Input
              value={workflow?.name || ""}
              onChange={(e) => setWorkflow(w => ({ ...w, name: e.target.value }))}
              className="bg-transparent border-none text-white font-semibold text-lg h-auto p-0 focus-visible:ring-0 max-w-xs"
              placeholder="Workflow name"
            />
            <span className="text-xs text-slate-500">{user?.name || "User"}'s workspace</span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={saveWorkflow}
              disabled={saving}
              className="text-slate-400 hover:text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span className="ml-2">Save</span>
            </Button>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      onClick={runWorkflow}
                      disabled={running || !canRunWorkflow()}
                      className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                      data-testid="run-workflow-btn"
                    >
                      {running ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Play className="w-4 h-4 mr-2" />
                      )}
                      Run
                    </Button>
                  </span>
                </TooltipTrigger>
                {!canRunWorkflow() && !running && (
                  <TooltipContent side="bottom" className="bg-slate-900 border-slate-700">
                    <p className="text-sm">Connect all required nodes to run the workflow</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>

            <Button variant="outline" size="sm" className="border-slate-700 text-slate-300">
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDragOver={onDragOver}
            onDrop={onDrop}
            nodeTypes={nodeTypes}
            defaultEdgeOptions={defaultEdgeOptions}
            connectionLineType={ConnectionLineType.SmoothStep}
            connectionLineStyle={connectionLineStyle}
            fitView
            className="bg-[#000000]"
            minZoom={0.1}
            maxZoom={2}
            snapToGrid
            snapGrid={[20, 20]}
          >
            <Background color="#121212" gap={40} size={1} />
            <Controls className="bg-slate-900 border-slate-700 rounded-lg overflow-hidden" />
            <MiniMap
              nodeColor={() => "#3D6355"}
              className="bg-slate-900/80 border border-slate-700 rounded-lg"
              maskColor="rgba(10, 10, 15, 0.8)"
            />

            {/* Queue indicator */}
            <Panel position="bottom-center">
              <div className="bg-slate-900/90 border border-slate-700 rounded-full px-4 py-2 flex items-center gap-3">
                <span className="text-sm text-slate-400">Queue</span>
                <span className="text-sm text-slate-300">{running ? "1 active" : "0 active"}</span>
                {running && <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />}
              </div>
            </Panel>
          </ReactFlow>
        </div>
      </div>

      {/* Help Panel */}
      {showHelp && (
        <div className="absolute bottom-24 right-6 w-80 bg-slate-900/95 backdrop-blur border border-slate-700 rounded-xl p-4 shadow-2xl">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-white">How to Use</h3>
            <Button variant="ghost" size="icon" onClick={() => setShowHelp(false)} className="h-6 w-6">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-3 text-sm text-slate-400">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded bg-indigo-500/20 flex items-center justify-center shrink-0">
                <Plus className="w-3 h-3 text-indigo-400" />
              </div>
              <p>Click the + button to add nodes to the canvas</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded bg-indigo-500/20 flex items-center justify-center shrink-0">
                <Layers className="w-3 h-3 text-indigo-400" />
              </div>
              <p>Drag from node handles to connect them</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded bg-indigo-500/20 flex items-center justify-center shrink-0">
                <Play className="w-3 h-3 text-indigo-400" />
              </div>
              <p>Click Run to execute the workflow</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
